import React, { useCallback, useEffect, useState } from 'react'
import { FormContext, useForm } from 'react-hook-form'
import { Box, Grid, Button, Tooltip } from '@material-ui/core'
import { useHistory } from 'react-router-dom'

import { Stepper, CompilationAlerts, StepContent } from './components'
import { LoadingFeedback } from 'components'

import {
  ageGateSchema,
  bannerAppearanceSchema,
  bannerConfigurationSchema,
  bannerLayoutSchema,
  cookieScannerSchema,
} from './stepSchemas'

import useCookieBanner from 'hooks/useCookieBanner'
import useStyles from './styles'

import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'

const BannerForm = () => {
  const classes = useStyles()
  const history = useHistory()
  const [submitLoading, setSubmitLoading] = useState(false)

  const {
    activeStep,
    banner,
    loading,
    isCompiling,
    isScanning,
    backStep,
    executeStepSubmit,
  } = useCookieBanner()

  const { STEPS } = constants.cookies.banner

  const stepSchemas = {
    [STEPS.CONFIGURATION]: bannerConfigurationSchema,
    [STEPS.APPEARANCE]: bannerAppearanceSchema,
    [STEPS.BANNER_LAYOUT]: bannerLayoutSchema,
    [STEPS.AGE_GATE]: ageGateSchema,
    [STEPS.SCANNER]: cookieScannerSchema,
  }

  const formMethods = useForm({
    validationSchema: stepSchemas[activeStep],
    defaultValues: helpers.cookies.banner.prepareFormDefaultValues(),
  })

  // Função que será chamada quando o botão "Continuar" for clicado
  const handleStepSubmit = async () => {
    setSubmitLoading(true)
    try {
      await executeStepSubmit()
    } finally {
      setSubmitLoading(false)
    }
  }

  useEffect(() => {
    if (banner) {
      const formValues = helpers.cookies.banner.prepareFormDefaultValues(banner)
      formMethods.reset(formValues)
    }
  }, [banner, activeStep])

  const handleCustomAction = useCallback(
    (actionType) => {
      if (actionType === 'edit_policy') {
        // Buscar a política atual do CookiePolicyCard
        service.dponet.privacyPolicies
          .getCookiePolicy({
            showContent: false,
          })
          .then((response) => {
            const policies = response?.data?.privacyPolicies || []
            const activePolicy = policies.find(
              (policy) =>
                policy.status === constants.documents.ACTIVE_STATUS_ID,
            )
            const policyToEdit = activePolicy || policies[0]

            if (policyToEdit?.id) {
              history.push(`/documents/${policyToEdit.id}/edit`)
            }
          })
          .catch((error) => {
            console.error('Erro ao buscar política para edição:', error)
          })
      }
    },
    [history],
  )

  return (
    <FormContext {...formMethods}>
      <LoadingFeedback open={loading} />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Stepper />
        </Grid>

        <Grid item xs={12}>
          <CompilationAlerts onCustomAction={handleCustomAction} />
        </Grid>

        <Grid item xs={12}>
          <StepContent />
        </Grid>

        <Grid item xs={12}>
          <Box className={classes.buttonContainer}>
            {activeStep > 0 && (
              <Button variant="outlined" onClick={backStep}>
                Voltar
              </Button>
            )}
            {activeStep < 7 && (
              <Tooltip
                title={
                  isCompiling
                    ? 'Aguarde, o banner está sendo compilado.'
                    : isScanning
                    ? 'Aguarde, o banner está sendo escaneado.'
                    : ''
                }
                arrow
                disableHoverListener={!(isCompiling || isScanning)}
              >
                <span>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleStepSubmit}
                    disabled={submitLoading || isCompiling || isScanning}
                  >
                    {helpers.cookies.banner.getButtonText(
                      activeStep,
                      banner,
                      isCompiling,
                    )}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Grid>
      </Grid>
    </FormContext>
  )
}

export default BannerForm
